/* lib/router.jsx — минимальный, API-совместимый с react-router-dom v6 роутер.
   ПРИЧИНА: в прототипе нет Vite-бандлера и надёжного UMD react-router-dom.
   Реализует ровно ту поверхность API, что использует прототип:
   BrowserRouter, Routes, Route, Outlet, Navigate, Link, NavLink,
   useNavigate, useLocation, useParams, useSearchParams.

   РЕЖИМ HISTORY (cutover на боевой :5173 / прод-домен). Маршрут живёт в реальном пути
   (`/kitchen?tab=recipes&edit=ttk-301`) через History API (pushState/replaceState), а не во
   фрагменте. ТРЕБУЕТ SPA-fallback на сервере (Vite dev — встроенный appType:'spa'; nginx —
   `try_files $uri /index.html`), иначе refresh/deep-link на вложенных путях дадут 404 хоста.
   Имена и сигнатуры — 1:1 с react-router v6; ссылки из писем/Робокассы (обычные пути,
   `/verify-email?…`, `/checkout/success?…`) теперь резолвятся напрямую. */

import React from 'react';
import { stackParams } from '../hooks/drawerStack.js';

  const { createContext, useContext, useState, useEffect, useCallback, useMemo, createElement } = React;

  // ---- история (hash) ----
  const LocationContext = createContext(null);
  const RouteContext = createContext({ outlet: null, params: {} });

  function useLocation() {
    return useContext(LocationContext).location;
  }
  function useNavigate() {
    return useContext(LocationContext).navigate;
  }

  /* Индекс записи истории (kitchen-ux A2, K-02). Роутер кладёт его в history.state
     (__rrIdx) при каждой записи: push — следующий, replace — тот же. Сторож «назад/вперёд»
     по индексам знает, на сколько шагов браузер ушёл, и ОТКАТЫВАЕТ переход (history.go),
     а не перезаписывает чужую запись адресом редактора — история остаётся целой.
     Запись без индекса (pushState не через роутер) получает следующий при синхронизации. */
  const readIdx = () => {
    const s = window.history.state;
    return s && typeof s.__rrIdx === 'number' ? s.__rrIdx : null;
  };
  let lastIdx = 0;                       // индекс записи, на которой стоит приложение
  const currentIndex = () => lastIdx;

  /* Пространство записи истории (kitchen-ux A2 круг 6, S10 — MAJOR верификатора, V1). Каждая запись
     роутера несёт пространство вкладки, в котором её сделали (__ws). Пространство вкладки роутеру
     сообщает AuthProvider (setHistoryWs) — до первого выбора его нет, запись помечается пустым (null).
     Переход «назад/вперёд» на запись ЧУЖОГО пространства с кадрами drawer-стека (?edit=, ?etab, ?v)
     снимает кадры до рендера: запись заменяется тем же адресом без них. Иначе кадр прежнего
     пространства открылся бы в новом (404 поверх реестра; у кадров кассы id могут совпасть — тогда
     чужая позиция). Запись без пространства (ссылка, запись старше правила) — не трогается. */
  let historyWs = null;
  function setHistoryWs(ws) { historyWs = ws == null ? null : ws; }
  const currentHistoryWs = () => historyWs;
  let syncLocation = null;               // BrowserRouter: перечитать адрес после замены записи

  /* Адрес записи без кадров стека, если запись — чужого пространства и кадры в ней есть; иначе null. */
  function foreignFramesSearch(state, search, ws) {
    const own = state && state.__ws != null ? state.__ws : null;
    if (own == null || ws == null || String(own) === String(ws)) return null;
    const sp = new URLSearchParams(search || '');
    if (!sp.has('edit')) return null;
    const rest = stackParams(sp, []).toString();
    return rest ? '?' + rest : '';
  }
  // текущая запись — без кадров чужого пространства (replace, пространство — вкладки) → снимали ли
  function dropForeignFrames(ws = historyWs) {
    const st = window.history.state;
    const next = foreignFramesSearch(st, window.location.search, ws);
    if (next == null) return false;
    window.history.replaceState({ ...(st || {}), __ws: ws }, '', window.location.pathname + next);
    return true;
  }
  /* Первое пространство вкладки (загрузка, вход): запись, восстановленная после перезагрузки, с кадрами
     чужого пространства — без кадров; запись без пространства (ссылка) — помечается им как есть. */
  function settleEntryWs(ws) {
    historyWs = ws == null ? null : ws;
    if (historyWs == null || typeof window === 'undefined' || !window.history) return;
    const st = window.history.state;
    if (dropForeignFrames(historyWs)) { if (syncLocation) syncLocation(); return; }
    if (!st || st.__ws == null) {
      window.history.replaceState({ ...(st || {}), __ws: historyWs }, '', window.location.pathname + window.location.search);
    }
  }

  // logical-маршрут берём из реального пути (history-режим): pathname + search
  function readLocation() {
    return {
      pathname: window.location.pathname || '/',
      search: window.location.search || '',
      hash: '',
      state: (window.history.state && window.history.state.__rr) || null,
      idx: readIdx(),
    };
  }

  // logical target (строка «/kitchen?tab=…») → запись в history как реальный путь
  function writeHash(target, opts = {}) {
    const url = target.startsWith('/') ? target : '/' + target;
    const cur = readIdx();
    const base = cur == null ? lastIdx : cur;
    const idx = opts.replace ? base : base + 1;
    const st = { __rr: opts.state ?? null, __rrIdx: idx, __ws: opts.ws !== undefined ? opts.ws : historyWs };
    if (opts.replace) window.history.replaceState(st, '', url);
    else window.history.pushState(st, '', url);
    lastIdx = idx;
  }

  // запись без индекса (не наша) — получает следующий за текущим
  function stampIdx() {
    const cur = readIdx();
    if (cur != null) { lastIdx = cur; return; }
    const st = window.history.state;
    const idx = lastIdx + 1;
    window.history.replaceState({ __rr: (st && st.__rr) ?? null, __rrIdx: idx, __ws: st && st.__ws != null ? st.__ws : historyWs }, '', window.location.pathname + window.location.search);
    lastIdx = idx;
  }

  // адрес цели перехода — {pathname, search} (для сторожа несохранённого)
  function targetLocation(target) {
    const i = target.search(/[?#]/);
    const pathname = i < 0 ? target : target.slice(0, i);
    const rest = i < 0 ? '' : target.slice(i);
    const h = rest.indexOf('#');
    const search = h < 0 ? rest : rest.slice(0, h);
    return { pathname: pathname || '/', search: search.startsWith('?') ? search : '', state: null, idx: null };
  }

  /* Сторожа переходов (kitchen-ux A2, K-01/K-02). Один на приложение — ставит
     UnsavedChangesProvider.
     · popstate: роутер зовёт сторожа ДО синхронизации адреса с приложением; вернул true —
       переход перехвачен (сторож откатил его сам и спросит пользователя), состояние роутера
       не меняется. Слушатель на window не годится: провайдер монтируется после загрузки
       сессии, слушатель роутера успевал первым, и форма размонтировалась раньше вопроса.
     · navigate: любой переход приложения (палитра Ctrl+K, «Редактировать в чате», ссылки
       уведомлений, редиректы) — guard(from, to, proceed) вернул true: перехвачен, proceed()
       выполнит его после ответа «Сохранить / Отбросить»; «Отмена» — перехода нет.
     · opts.force — переход уже решён раньше (круг 5: оболочка снимает кадры drawer-стека при
       смене пространства, сторож к этому моменту пройден или форма чистая): мимо сторожа. */
  let popstateGuard = null;
  function setPopstateGuard(fn) {
    popstateGuard = typeof fn === 'function' ? fn : null;
    return () => { if (popstateGuard === fn) popstateGuard = null; };
  }
  function routerPopstate(event, apply) {
    if (popstateGuard && popstateGuard(event)) return false;
    apply();
    return true;
  }
  let navigateGuard = null;
  function setNavigateGuard(fn) {
    navigateGuard = typeof fn === 'function' ? fn : null;
    return () => { if (navigateGuard === fn) navigateGuard = null; };
  }
  function routerNavigate(target, opts, go) {
    if (navigateGuard && !(opts && opts.force) && navigateGuard(readLocation(), targetLocation(target), go, opts)) return false;
    go();
    return true;
  }

  function BrowserRouter({ children }) {
    const [location, setLocation] = useState(() => readLocation());

    useEffect(() => {
      // запись чужого пространства теряет кадры стека ДО синхронизации — экран их не увидит
      const sync = (e) => routerPopstate(e, () => { stampIdx(); dropForeignFrames(); setLocation(readLocation()); });
      syncLocation = () => setLocation(readLocation());
      window.addEventListener('popstate', sync);
      // history-режим: путь уже канонический; нормализуем router-state и индекс записи на старте
      const st = window.history.state;
      if (!(st && '__rr' in st) || typeof st.__rrIdx !== 'number') {
        window.history.replaceState(
          { ...(st && st.__ws != null ? { __ws: st.__ws } : {}), __rr: (st && st.__rr) ?? null, __rrIdx: st && typeof st.__rrIdx === 'number' ? st.__rrIdx : 0 },
          '', window.location.pathname + window.location.search,
        );
      }
      lastIdx = readIdx() ?? 0;
      return () => {
        window.removeEventListener('popstate', sync);
        syncLocation = null;
      };
    }, []);

    const navigate = useCallback((to, opts = {}) => {
      if (typeof to === 'number') { window.history.go(to); return; }
      const target = resolveTo(to);
      routerNavigate(target, opts, () => {
        // счётчик переходов внутри приложения (для useBackNav: отличить deep-link от истории).
        // считаем только push'и (не replace, не редиректы Navigate) — реальные шаги пользователя.
        if (!opts.replace) window.__rrNavDepth = (window.__rrNavDepth || 0) + 1;
        writeHash(target, opts);
        setLocation(readLocation()); // pushState/replaceState не шлют событий — обновляем вручную
        if (!opts.replace && !opts.preventScrollReset) {
          const main = document.querySelector('[data-scroll-main]');
          if (main) main.scrollTop = 0;
        }
      });
    }, []);

    const value = useMemo(() => ({ location, navigate }), [location, navigate]);
    return createElement(LocationContext.Provider, { value }, children);
  }

  // объект/строка → logical-строка «/path?search#hash»
  function resolveTo(to) {
    if (typeof to === 'string') return to.startsWith('/') ? to : '/' + to;
    const { pathname = '/', search = '', hash = '' } = to;
    const s = search ? (search.startsWith('?') ? search : '?' + search) : '';
    const h = hash ? (hash.startsWith('#') ? hash : '#' + hash) : '';
    return (pathname.startsWith('/') ? pathname : '/' + pathname) + s + h;
  }

  // ---- сбор конфигурации маршрутов из JSX-детей ----
  function childrenToRoutes(children) {
    const routes = [];
    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;
      const { path, index, element } = child.props;
      const route = {
        path: index ? undefined : path,
        index: !!index,
        element: element ?? null,
        children: child.props.children ? childrenToRoutes(child.props.children) : [],
      };
      routes.push(route);
    });
    return routes;
  }

  // разворачиваем дерево в плоские «ветви» (root → leaf)
  function flattenRoutes(routes, parent = { fullPath: '', chain: [] }) {
    let branches = [];
    routes.forEach((route) => {
      const seg = route.index ? '' : (route.path || '');
      const joined = joinPaths(parent.fullPath, seg);
      const chain = parent.chain.concat(route);
      if (route.children && route.children.length) {
        branches = branches.concat(flattenRoutes(route.children, { fullPath: joined, chain }));
      } else {
        branches.push({ fullPath: joined || '/', chain, index: route.index, raw: seg });
      }
    });
    return branches;
  }

  function joinPaths(a, b) {
    if (!b) return a || '';
    if (!a) return b.startsWith('/') ? b : '/' + b;
    return (a.replace(/\/$/, '') + '/' + b.replace(/^\//, ''));
  }

  // компиляция паттерна → regex + ключи (посегментно, корректно для * и :param)
  function compile(pattern) {
    const keys = [];
    let p = pattern.replace(/\/$/, '');
    if (p === '') p = '/';
    const regexStr = p.split('/').map((seg) => {
      if (seg === '') return '';
      if (seg === '*') { keys.push('*'); return '(.*)'; }
      if (seg.startsWith(':')) { keys.push(seg.slice(1)); return '([^/]+)'; }
      return seg.replace(/[.+?^${}()|[\]\\]/g, '\\$&');
    }).join('/');
    return { regex: new RegExp('^' + regexStr + '/?$'), keys, pattern: p };
  }

  // ранжирование: больше статических сегментов → выше; splat → ниже
  function score(branch) {
    const segs = branch.fullPath.split('/').filter(Boolean);
    let s = 0;
    segs.forEach((seg) => {
      if (seg === '*') s -= 4;
      else if (seg.startsWith(':')) s += 4;
      else s += 8;
    });
    if (branch.index) s += 2;
    return s;
  }

  function matchBranch(branch, pathname) {
    const { regex, keys } = compile(branch.fullPath);
    const m = regex.exec(pathname);
    if (!m) return null;
    const params = {};
    keys.forEach((k, i) => {
      const raw = m[i + 1] != null ? m[i + 1] : '';
      try { params[k] = decodeURIComponent(raw); } catch (e) { params[k] = raw; }
    });
    return params;
  }

  function Routes({ children }) {
    const { location } = useContext(LocationContext);
    const routes = useMemo(() => childrenToRoutes(children), [children]);
    const branches = useMemo(() => flattenRoutes(routes).sort((a, b) => score(b) - score(a)), [routes]);

    let matched = null;
    for (const branch of branches) {
      const params = matchBranch(branch, location.pathname);
      if (params) { matched = { branch, params }; break; }
    }
    if (!matched) return null;

    const chain = matched.branch.chain;
    function renderChain(i) {
      const route = chain[i];
      if (!route) return null;
      const outlet = renderChain(i + 1);
      const el = route.element != null ? route.element : createElement(Outlet, null);
      return createElement(RouteContext.Provider, { value: { outlet, params: matched.params } }, el);
    }
    return renderChain(0);
  }

  function Route() { return null; } // конфигурационный, не рендерится напрямую

  function Outlet() {
    const ctx = useContext(RouteContext);
    return ctx.outlet || null;
  }

  function useParams() {
    return useContext(RouteContext).params || {};
  }

  function Navigate({ to, replace = true, state }) {
    const navigate = useNavigate();
    useEffect(() => { navigate(to, { replace, state }); }, [to]);
    return null;
  }

  function Link({ to, children, className, onClick, ...rest }) {
    const navigate = useNavigate();
    // реальный путь → middle-click / «открыть в новой вкладке» дают корректный URL (history)
    const href = resolveTo(to);
    const handle = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      e.preventDefault();
      if (onClick) onClick(e);
      navigate(to);
    };
    return createElement('a', { href, className, onClick: handle, ...rest }, children);
  }

  function NavLink({ to, children, className, end, ...rest }) {
    const location = useLocation();
    const targetPath = (typeof to === 'string' ? to : to.pathname).split('?')[0].split('#')[0];
    const cur = location.pathname;
    const isActive = end
      ? cur === targetPath
      : cur === targetPath || cur.startsWith(targetPath.replace(/\/$/, '') + '/');
    const cls = typeof className === 'function' ? className({ isActive }) : className;
    const kids = typeof children === 'function' ? children({ isActive }) : children;
    return createElement(Link, {
      to,
      className: cls,
      'aria-current': isActive ? 'page' : undefined,
      'data-active': isActive ? '' : undefined,
      ...rest,
    }, kids);
  }

  // useSearchParams — обёртка над URLSearchParams синхронно с logical-location
  function useSearchParams() {
    const { location, navigate } = useContext(LocationContext);
    const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const setSearchParams = useCallback((next, opts = {}) => {
      // Функциональная форма обязана видеть АКТУАЛЬНЫЙ адрес, а не тот, что был на
      // рендере, где создан этот колбэк. navigate() пишет в history синхронно
      // (writeHash → pushState), а React-состояние роутера догоняет лишь следующим
      // рендером. Пока `prev` собирался из замкнутого location.search, два писателя
      // в ОДНОМ коммите затирали друг друга: в плеере «Академии» resume-эффект
      // дописывал ?lesson=…, а сторож возврата с оплаты тут же снимал ?purchase=,
      // видя адрес ДО первой правки, — и ученик после оплаты попадал на пустой
      // экран вместо своего урока. Дефект общий для приложения: он ждал любой
      // экран, где query пишут дважды за коммит.
      const live = readLocation();
      const sp = typeof next === 'function' ? next(new URLSearchParams(live.search)) : new URLSearchParams(next);
      const str = sp.toString();
      navigate({ pathname: live.pathname, search: str ? '?' + str : '', hash: '' }, { replace: opts.replace, preventScrollReset: true });
    }, [navigate]);
    return [searchParams, setSearchParams];
  }

  export const RR = {
    BrowserRouter, Routes, Route, Outlet, Navigate, Link, NavLink,
    useNavigate, useLocation, useParams, useSearchParams, setPopstateGuard, setNavigateGuard, currentIndex,
    setHistoryWs, settleEntryWs, currentHistoryWs,
  };
  export { routerPopstate, routerNavigate, targetLocation, foreignFramesSearch };
